import { generateBooleanSchema, generateColorSchema, generateMediaUrlSchema, generateObjectSchema, generateRootObjectSchema, generateStringSchema, generateUrlSchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec, ImageParticleSpec, ButtonParticleSpec, RichTextParticleSpec } from "components/cards/particles/YinzCam";

export const specification = new BaseAtomSpec("Image Background CTA", generateRootObjectSchema({
    container: generateAtomContainerSchema({
        defaultValues: { themeMode: 'overlay' },
        uneditableFields: { background: false }
    }),
    ctaType: generateStringSchema("CTA Type", "Select type of CTA", {
        choices: ['icon', 'button'],
        choiceTitles: ["Right Arrow Icon", "CTA Button"]
    }),
    ctaButtonsPlacement: generateStringSchema("CTA Buttons Placement", "Choose where CTA buttons are placed", {
        choices: ['overlay-right', 'title-right'],
        choiceTitles: ['Overlay Right', 'Title Row Right'],
        defaultValue: 'title-right'
    }),
    overlayPadding: generateStringSchema("Overlay Padding", "The padding (inset) of the overlay text/buttons relative to the background image.", {
        defaultValue: '2rem'
    }),
    layout: generateStringSchema("Layout Mode", "The visual layout to use for this CTA card.", {
        choices: ['default', 'overlay-card', 'split-collage'],
        choiceTitles: ['Default', 'Overlay Card', 'Split Collage'],
        defaultValue: 'default'
    }),
    link: generateUrlSchema("Link", "The link for this media item. The default link will be generated from the slug if this is not provided (and the slug is)."),
    isBodyTextRequired: generateBooleanSchema("Body Text", "Is body text required"),
    headerObject: generateStringSchema("Header Icon/Bar", "Header icon or bar selection.", {
        choices: ['', 'icon', 'bar'],
        choiceTitles: ['None', 'Icon', 'Bar'],
    }),
    headerIcon: ImageParticleSpec.generateSchema("Header Icon", "Select an icon for header", {
        defaultValues: {
            container: { fillWidth: true, height: '4.25rem', width: '4.25rem', themeMode: 'overlay' }
        }
    }),
    headerBarBackgroundColor: generateColorSchema("Header Bar Background Color", "Background color of header bar."),
    headerText: TextParticleSpec.generateSchema("Heading Text", "Please enter a text for Heading", {
        defaultValues: { size: '2.25rem', style: 'normal', weight: '900' }
    }),
    useHeaderRichText: generateBooleanSchema("Use Heading Rich Text", "Use rich text HTML for the heading.", {
        defaultValue: false,
      }),
    headerRichText: RichTextParticleSpec.generateSchema("Heading Rich Text", "Please enter rich text for Heading", {
    defaultValues: {
        textDefaults: { size: '2.25rem', style: 'normal', weight: '900' },
    },
    }),
    bodyText: TextParticleSpec.generateSchema("Body Text", "Please enter a text for Body here", {
        defaultValues: { size: '1rem', style: 'normal', weight: '400' }
    }),
    useBodyRichText: generateBooleanSchema("Use Body Rich Text", "Use rich text HTML for the body.", {
        defaultValue: false,
      }),
    bodyRichText: RichTextParticleSpec.generateSchema("Body Rich Text", "Please enter rich text for Body here", {
    defaultValues: {
        textDefaults: { size: '1rem', style: 'normal', weight: '400' },
    },
    }),
    thumbnail: ImageParticleSpec.generateSchema("Thumbnail", "The image thumbnail for the background of the card.", {
        defaultValues: { sizing: 'cover', hoverZoomEnabled: true, role: 'general', fillWidth: true, fillHeight: true }
    }),
    secondaryImage1: ImageParticleSpec.generateSchema("Secondary Image 1", "The top image used in the split collage layout.", {
        defaultValues: { sizing: 'cover', hoverZoomEnabled: true, role: 'general', fillWidth: true, fillHeight: true },
    }),
    secondaryImage2: ImageParticleSpec.generateSchema("Secondary Image 2", "The bottom image used in the split collage layout.", {
        defaultValues: { sizing: 'cover', hoverZoomEnabled: true, role: 'general', fillWidth: true, fillHeight: true },
    }),
    ctaButton: ButtonParticleSpec.generateSchema("CTA Button", "The CTA button.", {
        defaultValues: {
            showImage: false, container: {
                themeMode: 'primary', background: { visible: true, useHighlightColor: true }, border: { visible: true, radius: '8px' },
                padding: '0.75rem 1.25rem'
            }, text: { size: '1rem', weight: '600' }
        }
    }),
    ctaButton2: ButtonParticleSpec.generateSchema("CTA Button 2", "The second CTA button.", {
        defaultValues: {
            showImage: false, container: {
                themeMode: 'primary', background: { visible: true, useHighlightColor: true }, border: { visible: true, radius: '8px' },
                padding: '0.75rem 1.25rem'
            }, text: { size: '1rem', weight: '600' }
        }
    }),
    ctaButtonsLayout: generateStringSchema("CTA Buttons Layout", "Choose whether CTA buttons are stacked vertically or shown in a horizontal row.", {
        choices: ['vertical', 'horizontal'],
        choiceTitles: ['Vertical Stack', 'Horizontal Row'],
        defaultValue: 'vertical'
    }),
    ctaIcon: ImageParticleSpec.generateSchema("CTA Icon", "The CTA icon.", {
        defaultValues: { container: { fillWidth: true, height: '2.75rem', width: '2.75rem', themeMode: 'overlay' } }
    }),
    imageGradient: generateBooleanSchema("Background Gradient", "Apply gradient to the background image", {
        defaultValue: true
    }),
    gradientColor: generateColorSchema("Gradient Color", "Gradient Color"),
    overlayCardBackground: generateObjectSchema("Overlay Card Background", "Background for the floating card used in the overlay-card layout. Supports either a solid color or an image.", {
        color: generateColorSchema("Color", "Solid background color for the overlay card."),
        image: generateMediaUrlSchema("Image", "Optional background image for the overlay card."),
    }),
    overlayCardBorderRadius: generateStringSchema("Overlay Card Border Radius", "Border radius for the floating card used in the overlay-card layout."),
    overlayCardWidth: generateStringSchema("Overlay Card Width", "Width of the floating card used in the overlay-card layout."),
    overlayCardOffsetX: generateStringSchema("Overlay Card Offset X", "Exact distance from the left edge of the overall card to the overlay card."),
    overlayCardOffsetY: generateStringSchema("Overlay Card Offset Y", "Exact distance from the top edge of the overall card to the overlay card."),
    splitRatio: generateStringSchema("Split Ratio", "Left/right split ratio for the split-collage layout. Supports values like \"56%\" or \"56/44\"."),
    centerAlignContent: generateBooleanSchema("Center Align Content", "Center Align Content", {
        defaultValue: false
    }),
    topAnchorContent: generateBooleanSchema("Top Anchor Content", "Anchor the overlay text/buttons to the top of the card instead of the bottom. Use the overlay padding-top to set the distance from the top edge.", {
        defaultValue: false
    }),
    topCtaGap: generateStringSchema("Top CTA Gap", "Vertical gap between the body text and the CTA button(s). Only applies when Top Anchor Content is on (e.g. \"26px\"). Applied identically on all breakpoints. (The heading -> body gap is set via a margin in the body rich text HTML.)", {
        defaultValue: '26px'
    })
}));
